// @givmo/employer-module — sign-up REQUEST-ACCESS screen (/sign-up/request-access). EP-SUITE2.
//
// The join-or-create pivot destination: the caller's verified work-email domain already belongs to an
// ACTIVE company on Givmo, so instead of creating a duplicate they request access and an Owner
// approves. Reached two ways (both flag-gated), each having stashed the target company in the sign-up
// join store first:
//   - PROVIDER path: the details screen resolved REQUEST_ACCESS (verified OAuth email) BEFORE
//     registering, and routed here.
//   - EMAIL path: `signUpWithEmail` returned a `requestAccess` pivot (the COMPANY_DOMAIN_JOINABLE 409
//     the adapter caught WITHOUT rolling the Firebase user back), the email/password screen routed
//     to the OTP screen (`?join=true`) to verify the work email, and its post-confirm routed here.
// One route, two states: the REQUEST view (company identity + "Request access" → POST
// /admin/company/join-requests) and the PENDING view ("Request sent — an Owner will approve" + logout).
// Filing is idempotent server-side while the request is pending, and `filed` is remembered in the
// store so an in-session revisit shows the pending view. A hard refresh (or a return visit) clears
// the store; with no target, a VERIFIED caller with join-or-create on asks the backend again
// (EP-SUITE2-FIX3, below) and anyone else restarts at /sign-up. It is also where a signed-in,
// company-less email requester LANDS from any door (EP-SUITE2-FIX5, `companyless-landing.ts`).
//
// ★ THE REQUEST IS POSTED ONLY UNDER A VERIFIED CREDENTIAL (EP-SUITE2-FIX2 (b)). The intake fails
// closed on `email_verified=false` in the ID token's claims. Two guards, by construction:
//   1. the REQUEST view renders only for a Firebase user whose `emailVerified` flag is set (the
//      backend OTP confirm sets it on the record; `confirmOtp` reloads the user so the flag is
//      current). Anyone else is sent to the product's own verification screen (`/sign-up/otp?join=true`)
//      — the honest state for an unverified caller — never shown a button that cannot succeed;
//   2. the POST carries a FRESHLY MINTED token (`forceRefreshAuthHeader`): the api-client's Bearer
//      interceptor otherwise sends the SDK's cached token, which for an email sign-up still carries
//      the pre-confirm `email_verified=false` claim for up to an hour.
// The OAuth path arrives verified (Google attests; Microsoft repaired on the OTP screen first), so
// for it guard 1 is a no-op and the screen renders exactly as before.
//
// D-EP-60 (5): it is also where a signed-in caller whose company is ALREADY on Givmo lands (the details
// screen hands such a caller here; so does a door the funnel's table cannot place, a company SSO
// sign-in's account). With a company to request — join-or-create on, the resolve naming it — the
// REQUEST view, as always; anywhere it would otherwise send the caller on to register (the details
// screen) or to start over (`/sign-up`), an account that works for a company on Givmo
// (`employer-of-record.ts`) gets the INVITE view instead: that company, "Your team is already on
// Givmo", and "Ask a Global Admin of {company} to invite you." (the host's own word for the role). It
// files nothing, so it needs no verified credential, and its sign-out leaves for the host's sign-in
// door (EP-MNT8-FIX1): while it is on its way there, this screen's own restart of a signed-out visitor
// stands down (`leavingForSignIn`, below), or the sign-out's commit would send them to `/sign-up`.
//
// Rendered by both hosts (EP-MNT8): their auth, addresses, look and frame come from `useSignUpHost()`.

import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ErrorMessage, Select, testIdProp } from '@givmo/design-system';
import { sdk, forceRefreshAuthHeader } from '@givmo/api-client';
import { COMPANY_ONBOARDING_OUTCOME, type OnboardingResolution } from '@givmo/auth';
import { productLabel } from '../../lib/app-names';
import { isCompanyJoinOrCreateEnabled } from '../../lib/env';
import { SignUpHeading, useSignUpHost } from './sign-up-host';
import { useAuthError } from './use-auth-error';
import { useSignUpJoin } from './sign-up-join-context';
import { useEmployerOfRecord } from './employer-of-record';

/** EP-SUITE2-FIX3's resolve, keyed per uid: the answer belongs to one account's verified email. */
const RE_RESOLVE_KEY = 'admin-sign-up-request-access-re-resolve';

/**
 * EP-SUITE8's resolve after the email path's pivot, keyed per uid: the create's 409 carries the
 * company but not the apps a requester may ask for (`requestable_apps`), so the screen asks once.
 */
const APPS_RESOLVE_KEY = 'admin-sign-up-request-access-apps';

/**
 * The question's "said nothing" choice — sends no `requested_app`. Not the empty string: the Select
 * reads an empty value as no value and would show its placeholder instead of "Not sure".
 */
const NO_APP = 'not-sure';

/**
 * EP-SUITE2-FIX5 (the cut-6 review's P2): how long the screen stays blank asking. The api-client sets
 * no request timeout, so a hung resolve rendered nothing until the fetch itself gave up. The resolve
 * decides and provisions nothing, so answering it late is safe to ignore: past the bound the visit
 * reads as a failed resolve, and the details screen it then lands on asks again on submit.
 */
const RE_RESOLVE_TIMEOUT_MS = 8_000;

/**
 * EP-MNT8-FIX1 (a): set while the invite view's own sign-out takes the visitor to the sign-in door. The
 * SDK tells its listeners after the click has returned, so React commits the sign-out while the door is
 * still loading, and that commit runs this screen's restart of a signed-out visitor (`/sign-up`, the
 * register chooser) — twice where the identity boundary remounts the routes, the second time in a fresh
 * instance, which is why this lives at module scope (as the sign-up stores do). The restart, which
 * would supersede the door's navigation, stands down while it is set.
 */
let leavingForSignIn = false;

function withinBound<T>(work: Promise<T>): Promise<T | null> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(null), RE_RESOLVE_TIMEOUT_MS);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export function SignUpRequestAccessScreen() {
  const { auth, paths, look, words, Shell } = useSignUpHost();
  const { user, signOut, resolveDomain, establishAfterProvisioning } = auth;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { target, filed, requestableApps, setTarget, setFiled, setRequestableApps } =
    useSignUpJoin();
  // EP-SUITE8 (D-EP-69): the app the person says they need, if any — a NOTE for the Global Admin
  // who approves, never a grant. Nothing is chosen until they choose.
  const [requestedApp, setRequestedApp] = useState<string>(NO_APP);
  const { error, clearError, surface } = useAuthError();

  // The credential the request would ride. The pending view needs no credential check — the request
  // it reports was already accepted under a verified one.
  const credentialVerified = user?.emailVerified === true;
  const needsVerification = !filed && !credentialVerified;

  // EP-SUITE2-FIX3 ★ A MISSING TARGET IS RECOVERABLE FOR A VERIFIED CALLER. The store only ever held
  // what `POST /admin/company/resolve` answered for this caller's verified work email, so after a hard
  // refresh or a return visit (the store is in memory) that caller can simply ask again — the
  // existing request and pending views then render as before. Restarting at /sign-up instead was a
  // dead end: the work email is already registered, so the flow ended at /sign-in on "Could not load
  // your company" until an Owner approved. Re-filing is safe while the request is PENDING: the intake
  // returns it unchanged, with no second Owner email. After an Owner REJECTED it the resolve still
  // answers REQUEST_ACCESS (it never reads join requests), so a click files a new request and the
  // Owners are emailed again — the provider path already allows the same (no backend cooldown). Only
  // a VERIFIED caller asks (the resolve refuses an unverified one) and only with join-or-create on
  // (off, the routes 404 and nothing may change).
  const reResolvable =
    target === null && user !== null && credentialVerified && isCompanyJoinOrCreateEnabled();
  const reResolve = useQuery({
    queryKey: [RE_RESOLVE_KEY, user?.uid ?? null],
    queryFn: async (): Promise<OnboardingResolution | null> => {
      if (user === null) return null;
      try {
        // Never the cached token, as for the join POST below: the resolve fails closed on an
        // `email_verified=false` claim, and the confirm that verified this email may postdate it.
        return await withinBound(user.getIdToken(true).then(() => resolveDomain()));
      } catch {
        return null; // A failed resolve lands like one with nothing to join (below).
      }
    },
    enabled: reResolvable,
    retry: false,
    // One answer per visit: an Owner can approve between two, and the answer changes when they do.
    staleTime: 0,
    gcTime: 0,
  });

  // EP-SUITE8 (OM-80): the apps a requester may say they need. A resolve that pointed the pivot here
  // carried them in (the details screen's, the re-resolve above); the email path's pivot did not, so
  // the request view asks once, under a fresh token (the resolve fails closed on a stale
  // `email_verified=false` claim, as above). Bounded like the re-resolve; a failed, slow or listless
  // answer (a backend before OM-80) reads as no question, and the request view is never held past it.
  const appsReadable =
    target !== null &&
    !filed &&
    credentialVerified &&
    requestableApps === null &&
    user !== null &&
    isCompanyJoinOrCreateEnabled();
  const appsRead = useQuery({
    queryKey: [APPS_RESOLVE_KEY, user?.uid ?? null],
    queryFn: async (): Promise<readonly string[]> => {
      if (user === null) return [];
      try {
        const resolution = await withinBound(user.getIdToken(true).then(() => resolveDomain()));
        return resolution?.requestableApps ?? [];
      } catch {
        return [];
      }
    },
    enabled: appsReadable,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  useEffect(() => {
    if (appsRead.isSuccess && requestableApps === null) setRequestableApps(appsRead.data);
  }, [appsRead.isSuccess, appsRead.data, requestableApps, setRequestableApps]);

  // No target — re-resolve (above) or restart the flow. An unverified caller — verify first, on the
  // product's own OTP screen, which lands back here after the code. Every exit that leaves on its own
  // REPLACES this history entry: re-entered with Back the screen would decide the same way again and
  // bounce the visitor forward, so a pushed entry is a Back trap — the OTP detour included
  // (EP-SUITE2-FIX6, the cut-6 review's note), since an unverified caller is detoured on every entry.
  // D-EP-60 (5): the company this account already works for, asked only while there is no target.
  const employer = useEmployerOfRecord(target === null ? user : null);
  const resolution = reResolve.data;
  const resolvedNothing = reResolvable
    ? reResolve.isSuccess &&
      !reResolve.isFetching &&
      !(
        (resolution?.outcome === COMPANY_ONBOARDING_OUTCOME.requestAccess &&
          resolution.company !== null) ||
        resolution?.outcome === COMPANY_ONBOARDING_OUTCOME.alreadyMember ||
        resolution?.outcome === COMPANY_ONBOARDING_OUTCOME.enableProduct
      )
    : true;
  const inviteCompany =
    target === null && resolvedNothing && employer.settled ? employer.company : null;

  useEffect(() => {
    if (target !== null) {
      if (needsVerification) {
        void navigate({ to: paths.signUpOtp, search: { join: true }, replace: true });
      }
      return;
    }
    if (reResolvable) {
      if (!reResolve.isSuccess || reResolve.isFetching) return; // still asking
      const resolution = reResolve.data;
      switch (resolution?.outcome) {
        case COMPANY_ONBOARDING_OUTCOME.requestAccess:
          if (resolution.company === null) break; // malformed target → as if nothing to join
          setTarget(resolution.company, resolution.requestableApps ?? []);
          return;
        case COMPANY_ONBOARDING_OUTCOME.alreadyMember:
        case COMPANY_ONBOARDING_OUTCOME.enableProduct:
          // An Owner approved since the last visit: into the portal, as the details screen routes it.
          void navigate({ to: paths.dashboard, replace: true });
          return;
        default:
          break;
      }
      // EP-SUITE2-FIX5 ⚠ NEVER BACK TO /sign-up FROM HERE. That door now LANDS a signed-in,
      // company-less caller — this one — on this very screen, so the old restart would loop.
      // CREATE_AS_OWNER, BLOCKED, an unknown or malformed answer, or a failed resolve leave this
      // signed-in account one thing to do: register a company. The details screen's provider path does
      // that for the account already signed in, has no automatic exit, and its own submit resolves
      // again — a failed resolve heals, a joinable domain still pivots, a blocked one is refused with
      // the backend's own message. D-EP-60 (5): unless it already works for a company on Givmo — then
      // it stays for the invite view (below).
      if (!employer.settled || employer.company !== null) return;
      void navigate({
        to: paths.signUpDetails,
        search: { provider: true },
        replace: true,
      });
      return;
    }
    // Signed out, unverified, or join-or-create off: the restart, as before. For a signed-in caller
    // `/sign-up` then lands them — their OTP or the details screen, or the chooser when no funnel
    // screen fits — and none of those lands back here. D-EP-60 (5): an employee of a company on
    // Givmo stays for the invite view instead.
    if (!employer.settled || employer.company !== null) return;
    // EP-MNT8-FIX1 (a): the invite view's sign-out is already on its way to the sign-in door.
    if (leavingForSignIn) return;
    void navigate({ to: paths.signUp, replace: true });
  }, [
    target,
    needsVerification,
    reResolvable,
    reResolve.isSuccess,
    reResolve.isFetching,
    reResolve.data,
    employer.settled,
    employer.company,
    setTarget,
    navigate,
    paths,
  ]);

  const fileRequest = useMutation({
    mutationFn: async () => {
      const credential = user;
      if (credential === null) {
        throw new Error('Please sign in again to request access.');
      }
      // Never the cached token: the confirm that verified this email happened AFTER it was minted.
      const fresh = await forceRefreshAuthHeader(credential);
      // EXACTLY the person's own note or nothing: never `requested_product`, which stamped
      // Donations on every request (D-CPO-64); the approver chooses what they get.
      await sdk.companiesCreateCompanyJoinRequest({
        ...(fresh ?? {}),
        body: requestedApp === NO_APP ? {} : { requested_app: requestedApp },
        throwOnError: true,
      });
      return credential;
    },
    onSuccess: (credential) => {
      setFiled(true);
      // AS-SUITE10: the intake provisioned this requester's Givmo `user` row (inline, when absent)
      // before answering, so the row now CERTAINLY exists — for a Google requester the first point
      // it does. Hand the filing's account to the context's post-provisioning establish; it sends
      // nothing when this sign-in's attempt already landed (the email pivot's, at the 409), and
      // nothing at all once another account is signed in. Fire-and-forget: the pending view never
      // waits on the suite session.
      establishAfterProvisioning(credential);
    },
    onError: (thrown) => surface(thrown),
  });

  const onRequest = () => {
    clearError();
    fileRequest.mutate();
  };

  // Each sentence is the same few text runs it always was — split only around the company's name —
  // so the host's words change nothing else about how the portal sets it (a run boundary moves the
  // browser's kerning).
  const requestMiddle =
    ' already has a Givmo account for your work-email domain. Request access and ' +
    `${words.approver} of `;
  const sentLead = `We’ve let ${words.approvers} of `;
  const sentRest =
    ` know you’d like to join. You’ll get access ` +
    (words.accessedProduct === null ? '' : `to ${words.accessedProduct} `) +
    `once ${words.approver} approves your request.`;

  const signOutButton = (onSignOut: () => Promise<void>) => (
    <button
      type="button"
      onClick={() => void onSignOut()}
      className={look.textButton}
      {...testIdProp('auth-logout')}
    >
      {words.signOut}
    </button>
  );
  const logout = signOutButton(signOut);

  // EP-MNT8-FIX1 (a): the invite view's sign-out leaves for the host's sign-in door, the code screen's
  // way (EP-SUITE2-FIX5): the cache cleared first, then a replace. Signed out where it stands, this
  // screen restarts a signed-out visitor at the register chooser ("Create your admin account"), the
  // one place D-EP-60 (5) never sends an employee of a company on Givmo — and the SDK's emission is
  // committed while the door is still loading, so the restart stands down until the door has its
  // answer (`leavingForSignIn`). The request and pending views keep their plain sign-out, and with it
  // the restart.
  const onInviteSignOut = async () => {
    leavingForSignIn = true;
    try {
      await signOut();
      queryClient.clear();
      await navigate({ to: paths.loginHome, replace: true });
    } finally {
      leavingForSignIn = false;
    }
  };

  // D-EP-60 (5): the invite view — the company this account works for, and how to get in.
  if (inviteCompany !== null) {
    return (
      <Shell testId="page-sign-up-request-access">
        <div className="flex items-center gap-12" {...testIdProp('auth-request-access-company')}>
          {inviteCompany.logoUrl !== null && (
            <img
              src={inviteCompany.logoUrl}
              alt=""
              className="size-[40px] shrink-0 rounded-full object-cover"
              {...testIdProp('auth-request-access-logo')}
            />
          )}
          <span className={look.companyName} {...testIdProp('auth-request-access-name')}>
            {inviteCompany.name}
          </span>
        </div>

        <SignUpHeading heading={<h1 className={look.heading}>Your team is already on Givmo</h1>}>
          <p className={look.body} {...testIdProp('auth-request-access-invite')}>
            {`Ask ${words.approver} of ${inviteCompany.name} to invite you.`}
          </p>
        </SignUpHeading>

        {signOutButton(onInviteSignOut)}
      </Shell>
    );
  }

  // Re-resolving or redirecting (no target, or off to verify) — render nothing, as before: no flash
  // of the request view, and no new state to design. The same while the email path's one-time apps
  // read is out (bounded): the question must not appear under the person's pointer.
  if (target === null || needsVerification) return null;
  if (!filed && requestableApps === null && appsReadable) return null;

  if (filed) {
    return (
      <Shell testId="page-sign-up-request-access">
        <div {...testIdProp('auth-request-access-pending')} className="flex flex-col gap-16">
          <SignUpHeading
            heading={
              <h1 className={look.successHeading} {...testIdProp('auth-request-access-heading')}>
                Request sent
              </h1>
            }
          >
            <p className={look.body}>
              {sentLead}
              {target.name}
              {sentRest}
            </p>
          </SignUpHeading>
          {logout}
        </div>
      </Shell>
    );
  }

  return (
    <Shell testId="page-sign-up-request-access">
      <div className="flex items-center gap-12" {...testIdProp('auth-request-access-company')}>
        {target.logoUrl !== null && (
          <img
            src={target.logoUrl}
            alt=""
            className="size-[40px] shrink-0 rounded-full object-cover"
            {...testIdProp('auth-request-access-logo')}
          />
        )}
        <span className={look.companyName} {...testIdProp('auth-request-access-name')}>
          {target.name}
        </span>
      </div>

      <SignUpHeading heading={<h1 className={look.heading}>Your team is already on Givmo</h1>}>
        <p className={look.body}>
          {target.name}
          {requestMiddle}
          {target.name} will approve you.
        </p>
      </SignUpHeading>

      {/* EP-WEB58: asked only when there is a choice. With one app requestable (Donations alone, while
          Campaigns and Sales gifting are dark: OM-101, D-EP-75) the choice would be "Not sure" or
          that app, so the screen is the no-question state and the request carries no note. */}
      {requestableApps !== null && requestableApps.length > 1 && (
        <Select
          appearance="saas"
          label="Which app do you need? (optional)"
          value={requestedApp}
          onValueChange={(value) => setRequestedApp(value ?? NO_APP)}
          options={[
            { value: NO_APP, label: 'Not sure' },
            ...requestableApps.map((app) => ({ value: app, label: productLabel(app) })),
          ]}
          disabled={fileRequest.isPending}
          testId="auth-request-access-app"
        />
      )}

      {error !== null && <ErrorMessage testId="auth-error">{error}</ErrorMessage>}

      <Button
        {...look.primary}
        loading={fileRequest.isPending}
        disabled={fileRequest.isPending}
        onClick={onRequest}
        testId="auth-request-access-submit"
      >
        Request access
      </Button>

      {logout}
    </Shell>
  );
}
